import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
    Box, Typography, IconButton, Chip, TextField, useTheme, Tooltip, Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function ListRelationships({ refresh, onEdit, canModify, canDelete }) {
    const { chatbotId } = useParams();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [relationships, setRelationships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchRelationships();
    }, [chatbotId, refresh]);

    const fetchRelationships = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/knowledge-graph', {
                params: { chatbot_id: chatbotId, per_page: 500 }
            });
            if (response.data.success) {
                setRelationships(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching relationships:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this relationship?')) return;
        try {
            await axiosInstance.delete(`/api/knowledge-graph/${id}`, {
                params: { chatbot_id: chatbotId }
            });
            fetchRelationships();
        } catch (error) {
            console.error('Error deleting relationship:', error);
        }
    };

    const filteredData = useMemo(() => {
        if (!searchText) return relationships;
        const lower = searchText.toLowerCase();
        return relationships.filter(r =>
            r.source_entity.toLowerCase().includes(lower) ||
            r.target_entity.toLowerCase().includes(lower) ||
            r.relation_type.toLowerCase().includes(lower)
        );
    }, [relationships, searchText]);

    const columns = [
        {
            name: 'Source Entity',
            selector: row => row.source_entity,
            sortable: true,
            width: '180px',
            cell: row => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.source_entity}
                </Typography>
            )
        },
        {
            name: 'Relation',
            selector: row => row.relation_type,
            sortable: true,
            width: '180px',
            cell: row => (
                <Chip
                    icon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                    label={row.relation_type.replace(/_/g, ' ')}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            )
        },
        {
            name: 'Target Entity',
            selector: row => row.target_entity,
            sortable: true,
            width: '180px',
            cell: row => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.target_entity}
                </Typography>
            )
        },
        {
            name: 'Confidence',
            selector: row => row.confidence_score,
            sortable: true,
            width: '110px',
            cell: row => `${(row.confidence_score * 100).toFixed(0)}%`
        },
        {
            name: 'Source',
            selector: row => row.source_type,
            width: '100px',
            cell: row => (
                <Chip label={row.source_type || 'manual'} size="small" variant="outlined" />
            )
        },
        {
            name: 'Valid Until',
            selector: row => row.valid_until,
            sortable: true,
            width: '120px',
            cell: row => row.valid_until
                ? new Date(row.valid_until).toLocaleDateString()
                : '∞'
        },
        {
            name: 'Actions',
            width: '100px',
            cell: row => (
                <Box sx={{ display: 'flex' }}>
                    {canModify && (
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    ];

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search entities or relations..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ minWidth: 300 }}
                />
            </Box>

            <DataTable
                columns={columns}
                data={filteredData}
                progressPending={loading}
                pagination
                paginationPerPage={20}
                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                highlightOnHover
                dense
                theme={isDark ? 'dark' : 'default'}
                customStyles={{
                    headCells: { style: { fontWeight: 600, fontSize: '0.8rem' } },
                    cells: { style: { fontSize: '0.85rem' } },
                }}
                noDataComponent={
                    <Alert severity="info" sx={{ m: 2, width: '100%' }}>
                        No entity relationships found. Relationships are automatically created when content is added or edited.
                    </Alert>
                }
            />
        </Box>
    );
}
